import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';

import { Icon, Tooltip } from '../../../../main/scripts/lumen';
import { StatusPill } from './StatusPill';
import { ProjectKebabMenu } from './ProjectKebabMenu';

export const ProjectCard = ({
  variant = 'grid',
  status,
  name,
  description,
  recordCount,
  createdAt,
  href,
  onClickCard,
  onAddData,
  onCopy,
  onRename,
  onDelete,
  statusMessage,
  warning
}) => {
  const showKebab = onCopy || onRename || onDelete;

  const [nameTruncated, setNameTruncated] = useState(false);
  const titleRef = useCallback(
    node => {
      if (node) {
        setNameTruncated(node.scrollHeight > node.clientHeight + 1);
      }
    },
    [name, variant]
  );

  return (
    <Card
      as={href ? 'a' : 'div'}
      href={href || undefined}
      onClick={onClickCard}
      $variant={variant}
    >
      <TopRow>
        <StatusPill variant={status?.variant}>{status?.label}</StatusPill>
        {warning && (
          <Tooltip label={warning}>
            <WarningBadge
              role="img"
              aria-label={warning}
              data-test-id="project-warning"
            >
              <Icon name="circle-exclamation" size={16} />
            </WarningBadge>
          </Tooltip>
        )}
        {showKebab && (
          <KebabWrap onClick={stop}>
            <ProjectKebabMenu
              onCopy={onCopy}
              onRename={onRename}
              onDelete={onDelete}
            />
          </KebabWrap>
        )}
      </TopRow>

      <TitleTooltip label={name} disabled={!nameTruncated}>
        <Title ref={titleRef} $variant={variant}>
          {name}
        </Title>
      </TitleTooltip>
      {description && (
        <Description $variant={variant}>{description}</Description>
      )}

      <Meta>
        <MetaItem>
          <Icon name="file-lines" size={16} />
          <span>{recordCount}</span>
        </MetaItem>
        <MetaItem>
          <Icon name="calendar" size={16} />
          <span>{createdAt}</span>
        </MetaItem>
      </Meta>

      {statusMessage && (
        <StatusMessage title={statusMessage}>{statusMessage}</StatusMessage>
      )}

      {onAddData && (
        <AddDataButton
          type="button"
          onClick={event => {
            stop(event);
            onAddData();
          }}
        >
          <Icon name="plus" size={16} />
          <span>Add Data</span>
        </AddDataButton>
      )}
    </Card>
  );
};

const stop = event => {
  event.stopPropagation();
  event.preventDefault();
};


const Card = styled.a`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ $variant }) => ($variant === 'list' ? '16px' : '24px')};
  padding: ${({ $variant }) => ($variant === 'list' ? '24px' : '16px')};
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.slate[200]};
  border-radius: ${({ theme }) => theme.radius['2xl']};
  color: inherit;
  text-decoration: none;
  cursor: ${({ href }) => (href ? 'pointer' : 'default')};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }
`;

const TitleTooltip = styled(Tooltip)`
  display: block;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

/* Sits beside the status pill: the project works, this is a caveat about it. */
const WarningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: auto;
  color: ${({ theme }) => theme.color.amber[600]};
`;

const KebabWrap = styled.div`
  display: inline-flex;
`;

const Title = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.fg.default};
  display: -webkit-box;
  -webkit-line-clamp: ${({ $variant }) => ($variant === 'list' ? 1 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const Description = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #595959;

  display: -webkit-box;
  -webkit-line-clamp: ${({ $variant }) => ($variant === 'list' ? 1 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  color: #595959;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  & > svg {
    color: #595959;
  }
`;

const StatusMessage = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13px;
  color: ${({ theme }) => theme.color.fg.subtle};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const AddDataButton = styled.button`
  align-self: flex-start;
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary.base};

  &:hover {
    color: ${({ theme }) => theme.color.primary.hover};
  }

  & > svg {
    color: currentColor;
  }
`;
